import useMedia from 'common/hooks/useMedia'
import { fNumber } from 'common/utils/formatNumber'
import { useGetSummaryToday } from 'modules/deadShrimp/api/queries'
import { useSpeciesName } from 'modules/pond/api/selector'
import React from 'react'
import { useTranslation } from 'react-i18next'

import AssignmentIcon from '@mui/icons-material/Assignment'
import ContentPasteIcon from '@mui/icons-material/ContentPaste'
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied'
import { Box, Typography, Stack, useTheme } from '@mui/material'

type StatCardProps = {
  icon: React.ReactNode
  label: string
  value: string | number
  unit?: string
  valueColor?: string
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, unit, valueColor }) => {
  const theme = useTheme()
  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.primary.main}`,
        borderRadius: 2,
        px: 3,
        pt: 3,
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          backgroundColor: theme.palette.primary.main,
          width: '48px',
          minWidth: '48px',
          height: '48px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: 1.5,
          color: 'white',
          fontSize: 20,
        }}
      >
        {icon}
      </Box>
      <Box textAlign="right">
        <Typography fontSize={18} color={theme.palette.grey[600]}>
          {label}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            lineHeight: 1,
            color: valueColor,
            textAlign: 'right',
            fontSize: 20,
          }}
        >
          <Typography fontSize={40} fontWeight={600} mb={1} noWrap>
            {value}
          </Typography>
          {unit && (
            <Typography fontSize={14} fontWeight={600} sx={{ fontSize: 22 }}>
              {unit}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  )
}

const DeadSummaryTodayCard: React.FC = () => {
  const { t } = useTranslation('deadshrimp')
  const speciesName = useSpeciesName()
  const theme = useTheme()
  const { data: deadToday, isLoading } = useGetSummaryToday()
  const isTablet = useMedia('md')
  const isMobile = useMedia('sm')
  const mortalityInKg =
    speciesName == 'กุ้ง' ? '/static/icons/pond/shrimp.svg' : '/static/icons/pond/fish.svg'
  return (
    <>
      {isMobile ? (
        <Box
          sx={{
            border: `1px solid ${theme.palette.primary.main}`,
            borderRadius: 2,
            p: 1.5,
            width: '100%',
            minWidth: 0,
            boxShadow: 1,
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Box
              sx={{
                backgroundColor: theme.palette.primary.main,
                width: 30,
                height: 30,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: 1.5,
                color: 'white',
                fontSize: 24,
              }}
            >
              <AssignmentIcon />
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography fontSize={18} color={theme.palette.grey[600]}>
                {t('deadToday', { speciesName })}
              </Typography>
              <Box
                color={theme.palette.error.main}
                sx={{ display: 'flex', alignItems: 'baseline' }}
              >
                <Typography fontWeight={600} fontSize={36}>
                  {deadToday?.mortalityInKg ?? '0'}
                </Typography>
                <Typography fontSize={18} sx={{ ml: 1 }}>
                  {t('kg')}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}
          >
            <Typography fontSize={16} color="text.secondary">
              {t('accumulatedDead')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
              <Typography fontWeight={700} fontSize={22}>
                {deadToday?.accumulatedMortality ? fNumber(deadToday.accumulatedMortality) : '0'}
              </Typography>
              <Typography fontWeight={600} fontSize={16} sx={{ ml: 1 }}>
                {t('kg')}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}
          >
            <Typography fontSize={16} color="text.secondary">
              {speciesName + t('deadComparedToYesterday')}
            </Typography>
            <Box
              sx={{ display: 'flex', alignItems: 'baseline' }}
              color={
                (deadToday?.mortalityPercentage ?? 0) > 0
                  ? theme.palette.error.main
                  : theme.palette.success.main
              }
            >
              <Typography fontWeight={700} fontSize={22}>
                {deadToday?.mortalityPercentage ?? '0'}
              </Typography>
              <Typography fontWeight={600} fontSize={16} sx={{ ml: 1 }}>
                %
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : (
        <Stack
          direction={isTablet ? 'column' : 'row'}
          spacing={2}
          justifyContent="stretch"
          alignItems="stretch"
          sx={{ mb: 3 }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <StatCard
              icon={<img src={mortalityInKg} alt="dead shrimp" style={{ width: 24, height: 24 }} />}
              label={t('deadToday', { speciesName })}
              value={fNumber(deadToday?.mortalityInKg ?? 0)}
              unit={t('kg')}
              valueColor={theme.palette.error.main}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <StatCard
              icon={<SentimentDissatisfiedIcon />}
              label={t('accumulatedDead')}
              value={
                deadToday?.accumulatedMortality ? fNumber(deadToday.accumulatedMortality) : '0'
              }
              unit={t('kg')}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <StatCard
              icon={<ContentPasteIcon />}
              label={speciesName + t('deadComparedToYesterday')}
              value={fNumber(deadToday?.mortalityPercentage ?? 0)}
              unit="%"
              valueColor={
                (deadToday?.mortalityPercentage ?? 0) > 0
                  ? theme.palette.error.main
                  : theme.palette.success.main
              }
            />
          </Box>
        </Stack>
      )}
    </>
  )
}
export default DeadSummaryTodayCard
